import { Button, Form, Input, Modal, Select, Tabs } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import IntlMessage from '../../components/IntlMessage';
import vehiclesApi from '../../api/vehicles';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import UploadImage from '../../components/Elements/UploadImage';
import { debounce } from 'lodash';
import operatorsApi from '../../api/operators';
import seatMapApi from '../../api/seat-map';
import { useMounted } from '../../hooks/useMounted';

const { Option } = Select;

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

const VehicleForm = (props) => {
  const { onClose, currentData, open } = props;

  const { isMounted } = useMounted();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const intl = useIntl();
  const [imgs, setImgs] = useState(null);
  const [operators, setOperators] = useState([]);
  const [seatMaps, setSeatMaps] = useState([]);
  const [operatorSelected, setOperatorSelected] = useState(null);

  let initialValues = {
    ...currentData,
    operator_id: currentData?.operator?.id ?? null,
    seatmap_id: currentData?.seatmap?.id ?? null,
  };

  const fetch = useCallback(async () => {
    if (isMounted.current) {
      const filter = {
        page: 1,
        limit: 9999,
        keyword: '',
        order_by: 'title',
        order_dir: 'ASC',
      };
      const operatorRes = await operatorsApi.getAll(filter);
      const operatorData = operatorRes?.data;
      if (Array.isArray(operatorData)) {
        setOperators(operatorData);
      }

      // const seatMapRes = await seatMapApi.getAll(filter);
      // const seatMapData = seatMapRes?.data;
      // if (Array.isArray(seatMapData)) {
      //   setSeatMaps(seatMapData);
      // }
    }
  }, [isMounted]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleSubmit = () => {
    setLoading(true);
    form
      .validateFields()
      .then((values) => {
        // console.log('submit', values);
        const data = { ...values };
        delete data?.image;

        const payload = new FormData();
        Object.keys(data).forEach((key) => {
          payload.append(key, data[key]);
        });
        if (imgs) {
          payload.append('image', imgs);
        }

        if (currentData) {
          vehiclesApi
            .update(currentData.id, payload)
            .then((res) => {
              if (res?.statusCode === 400) {
                Modal.error({
                  title: res?.message,
                  okText: intl.formatMessage({ id: 'global.close' }),
                });
                setLoading(false);
              } else {
                setLoading(false);
                handleCancel();
                props.onReload();
              }
            })
            .catch((error) => {
              console.error(error);
              setLoading(false);
            });
        } else {
          vehiclesApi
            .create(payload)
            .then((res) => {
              if (res?.statusCode === 400) {
                Modal.error({
                  title: res?.message,
                  okText: intl.formatMessage({ id: 'global.close' }),
                });
                setLoading(false);
              } else {
                setLoading(false);
                handleCancel();
                props.onReload();
              }
            })
            .catch((error) => {
              console.error(error);
              setLoading(false);
            });
        }
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  };

  const onFinishFailed = (errorInfo) => {
    console.error('Failed:', errorInfo);
  };

  const handleCancel = () => {
    onClose();
  };

  const onChangeImage = (v, f) => {
    setImgs(f);
  };

  const fileList = useMemo(
    () =>
      currentData && currentData.image
        ? [
            {
              uid: '1',
              name: 'image.png',
              status: 'done ',
              url: currentData.image,
            },
          ]
        : [],
    [currentData]
  );

  const handleSearchOperator = debounce(async (value) => {
    try {
      const payload = {
        page: 1,
        limit: value === '' ? 10 : 9999,
        keyword: value || '',
        order_by: 'title',
        order_dir: 'ASC',
      };
      const res = await operatorsApi.getAll(payload);
      let operatorData = res?.data;

      if (Array.isArray(operatorData)) {
        setOperators(operatorData);
      }
    } catch (error) {
      console.error(error);
    }
  }, 300);

  const onChangeOperator = async (e) => {
    setOperatorSelected(e);
    form.setFieldsValue({ seatmap_id: null });
    const payload = {
      page: 1,
      limit: 9999,
      keyword: '',
      order_by: 'title',
      order_dir: 'ASC',
      filter_name: ['operator_id'],
      filter_value: [e],
    };
    const res = await seatMapApi.getAll(payload);
    const seatMapData = res?.data;

    if (Array.isArray(seatMapData)) {
      setSeatMaps(seatMapData);
    } else {
      setSeatMaps([]);
    }
  };

  const handleSearchSeatMap = debounce(async (value) => {
    try {
      if (operatorSelected) {
        const payload = {
          page: 1,
          limit: value === '' ? 10 : 9999,
          keyword: value || '',
          order_by: 'title',
          order_dir: 'ASC',
          filter_name: ['operator_id'],
          filter_value: [operatorSelected],
        };
        const res = await seatMapApi.getAll(payload);
        let seatMapData = res?.data;

        if (Array.isArray(seatMapData)) {
          setSeatMaps(seatMapData);
        }
      } else {
        setSeatMaps([]);
      }
    } catch (error) {
      console.error(error);
    }
  }, 300);

  const onChangeSeatMap = (e) => {
    // setSeatMapSelected(e);
  };

  const tabItems = [
    {
      key: '1',
      label: <IntlMessage id="global.vehicles" />,
      children: (
        <>
          <Form.Item
            name="operator_id"
            label={<IntlMessage id="global.operator" />}
            rules={[
              {
                required: true,
                message: <IntlMessage id="global.requiredfield" />,
              },
            ]}>
            <Select
              showSearch
              onChange={onChangeOperator}
              filterOption={false}
              onSearch={handleSearchOperator}
              placeholder={intl.formatMessage({
                id: 'placeholder.select_operator',
              })}>
              {operators.map((item, index) => {
                return (
                  <Option value={item.id} key={index}>
                    {item.title}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            name="seatmap_id"
            label={<IntlMessage id="global.seat_map" />}
            rules={[
              {
                required: true,
                message: <IntlMessage id="global.requiredfield" />,
              },
            ]}>
            <Select
              showSearch
              onChange={onChangeSeatMap}
              filterOption={false}
              onSearch={handleSearchSeatMap}
              placeholder={
                operatorSelected
                  ? intl.formatMessage({
                      id: 'placeholder.select_seat_map',
                    })
                  : 'Select an operator'
              }
              disabled={!operatorSelected}>
              {seatMaps.map((item, index) => {
                return (
                  <Option value={item.id} key={index}>
                    {item.title}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            label={<IntlMessage id="global.title" />}
            name="title"
            rules={[
              {
                required: true,
                whitespace: true,
                message: <IntlMessage id="global.requiredfield" />,
              },
            ]}>
            <Input placeholder="Input title" />
          </Form.Item>
          <Form.Item
            label={<IntlMessage id="global.seat" />}
            name="seat"
            rules={[
              {
                required: false,
              },
            ]}>
            <Input placeholder="Input seat" />
          </Form.Item>
          <Form.Item
            label={<IntlMessage id="global.description" />}
            name="description"
            rules={[
              {
                required: false,
              },
            ]}
            // valuePropName="data"
            // getValueFromEvent={(event, editor) => {
            //   const data = editor.getData();
            //   return data;
            // }}
          >
            {/* <CKEditor editor={ClassicEditor} /> */}
            <Input.TextArea
              placeholder="Input description"
              autoSize={{ minRows: 6 }}
            />
          </Form.Item>
          <Form.Item
            label={<IntlMessage id="global.image" />}
            name="image"
            rules={[
              {
                required: false,
              },
            ]}>
            <UploadImage onChangeData={onChangeImage} initList={fileList} />
          </Form.Item>
        </>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          currentData ? (
            <IntlMessage id="global.edit" />
          ) : (
            <IntlMessage id="global.add" />
          )
        }
        open={open}
        destroyOnClose={true}
        onCancel={handleCancel}
        width="80%"
        onOk={handleSubmit}
        footer={[
          <Button key="back" type="default" onClick={handleCancel}>
            {<IntlMessage id="global.cancel" />}
          </Button>,
          <Button
            key="submit"
            type="primary"
            htmlType="submit"
            loading={loading}
            onClick={handleSubmit}>
            {<IntlMessage id="global.save" />}
          </Button>,
        ]}>
        <Form
          {...layout}
          form={form}
          initialValues={initialValues}
          labelAlign="left"
          onFinish={handleSubmit}
          onFinishFailed={onFinishFailed}>
          <Tabs defaultActiveKey="1" items={tabItems}></Tabs>
        </Form>
      </Modal>
    </>
  );
};

export default VehicleForm;
